
import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { QuoteSection } from './components/QuoteSection';
import { BenefitsSection } from './components/BenefitsSection';
import { IntegrationSection } from './components/IntegrationSection';
import { TrustMarquee } from './components/TrustMarquee';
import { ServicesGrid } from './components/ServicesGrid';
import { IndustryMarquee } from './components/IndustryMarquee';
import { TechComparison } from './components/TechComparison';
import { ProcessTimeline } from './components/ProcessTimeline';
import { CaseStudies } from './components/CaseStudies';
import { FAQ } from './components/FAQ';
import { Footer } from './components/Footer';

// Core Pages
import { MedicinePage } from './components/pages/MedicinePage';
import { LawPage } from './components/pages/LawPage';
import { FinancePage } from './components/pages/FinancePage';
import { TradePage } from './components/pages/TradePage';
import { CraftPage } from './components/pages/CraftPage';
import { EstatePage } from './components/pages/EstatePage';
import { GastronomyPage } from './components/pages/GastronomyPage';
import { ContactPage } from './components/pages/ContactPage';
import { AboutPage } from './components/pages/AboutPage';
import { TrainingPage } from './components/pages/TrainingPage';
import { ROIPage } from './components/pages/ROIPage';
import { WebsitePage } from './components/pages/WebsitePage';
import { ArticlesPage } from './components/pages/ArticlesPage';
import { GlossaryPage } from './components/pages/GlossaryPage';

// Custom Architecture & Solutions
import { CustomArchitecturePage } from './components/pages/CustomArchitecturePage';
import { CustomSolutionsPage } from './components/pages/CustomSolutionsPage';
import { ReadyToGoPage } from './components/pages/ReadyToGoPage';
import { CookieConsent } from './components/CookieConsent';
import { GoogleAnalytics } from './components/GoogleAnalytics';

// Legal Pages
import { ImpressumPage } from './components/pages/ImpressumPage';
import { DatenschutzPage } from './components/pages/DatenschutzPage';
import { AGBPage } from './components/pages/AGBPage';

import { Page } from './types';

function App() {
  const [currentPage, setCurrentPage] = useState<Page>('home');

  // Handle initial URL load and browser back/forward buttons
  React.useEffect(() => {
    const handleLocationChange = () => {
      const path = window.location.pathname.substring(1); // Remove leading slash
      const validPages: Page[] = [
        'home', 'contact', 'about', 'training', 'websites',
        'ai-articles', 'ai-glossary', 'custom-architecture',
        'custom-solutions', 'ready-to-go', 'roi', 'medicine',
        'law', 'finance', 'trade', 'craft', 'estate', 'gastronomy',
        'impressum', 'privacy', 'agb'
      ];

      if (path === '' || path === 'index.html') {
        setCurrentPage('home');
      } else if (validPages.includes(path as Page)) {
        setCurrentPage(path as Page);
      }
    };

    handleLocationChange();

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  const navigateTo = (page: Page) => {
    setCurrentPage(page);

    // Update URL without reloading
    const url = page === 'home' ? '/' : `/${page}`;
    window.history.pushState({}, '', url);

    if (page === 'roi') {
      setTimeout(() => {
        const element = document.getElementById('calculator-start');
        if (element) element.scrollIntoView({ behavior: 'smooth' });
        else window.scrollTo(0, 0);
      }, 50);
    } else {
      window.scrollTo(0, 0);
    }
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-rabbit-bg font-sans selection:bg-clever-teal selection:text-white">
        <Header onNavigate={navigateTo} />
        <main>
          {currentPage === 'home' && (
            <>
              <Hero />
              <TrustMarquee />
              <QuoteSection />
              <BenefitsSection />
              <IntegrationSection />
              <ServicesGrid onNavigate={navigateTo} />
              <IndustryMarquee />
              <TechComparison />
              <ProcessTimeline />
              <CaseStudies />
              <FAQ />
            </>
          )}
          {currentPage === 'contact' && <ContactPage />}
          {currentPage === 'about' && <AboutPage onNavigate={navigateTo} />}
          {currentPage === 'training' && <TrainingPage onNavigate={navigateTo} />}
          {currentPage === 'websites' && <WebsitePage onNavigate={navigateTo} />}
          {currentPage === 'ai-articles' && <ArticlesPage onNavigate={navigateTo} />}
          {currentPage === 'ai-glossary' && <GlossaryPage onNavigate={navigateTo} />}
          {currentPage === 'custom-architecture' && <CustomArchitecturePage onNavigate={navigateTo} />}
          {currentPage === 'custom-solutions' && <CustomSolutionsPage onNavigate={navigateTo} />}
          {currentPage === 'ready-to-go' && <ReadyToGoPage onNavigate={navigateTo} />}
          {currentPage === 'roi' && <ROIPage />}
          {currentPage === 'medicine' && <MedicinePage onNavigate={navigateTo} />}
          {currentPage === 'law' && <LawPage onNavigate={navigateTo} />}
          {currentPage === 'finance' && <FinancePage onNavigate={navigateTo} />}
          {currentPage === 'trade' && <TradePage onNavigate={navigateTo} />}
          {currentPage === 'craft' && <CraftPage onNavigate={navigateTo} />}
          {currentPage === 'estate' && <EstatePage onNavigate={navigateTo} />}
          {currentPage === 'gastronomy' && <GastronomyPage onNavigate={navigateTo} />}

          {/* Legal Pages */}
          {currentPage === 'impressum' && <ImpressumPage />}
          {currentPage === 'privacy' && <DatenschutzPage />}
          {currentPage === 'agb' && <AGBPage />}
        </main>
        <Footer onNavigate={navigateTo} />
        <CookieConsent onNavigate={navigateTo} />
        <GoogleAnalytics />
      </div>
    </HelmetProvider >
  );
}

export default App;
